import { IDynamicLottieChange } from 'lottie-json-helper/lib/types';
import { IBasicVideoLayer, IVideoLayer, IVideoLayerInDTO, IVideoLayerOutDTO } from './layers/video-layer-model';
import { ILottieLayerInDTO, ILottieOutDTO } from './layers/lottie-model';
import { KeysMatching } from '../../defines';
import { IComposeAndPlayOrNot } from '../../job/editor-defines';
import { TakeRecorderManager } from '../controllers/take-recorder.manager';
import { VideoLayerIndexdbController } from 'src/app/models/project/controllers/video-layer-indexd.controller';
import { VideoLayerApiService } from 'src/app/services/api/auth/projects/video-layer/video-layer-api.service';
import { ProfileService } from 'src/app/services/show/profile.service';
import { ITranscript } from './transcript-model';
import { VideoEditTake } from '../../job/edit-job-schema';
import { ITrimLayers } from '../edit/edit-model';
import { ISceneContext } from '../scene-model';

export type TakeUpdateableProperties = Pick<
    ITakeOutDTO,
    'startTime' | 'endTime' | 'copy' | 'videoLayers' | 'duration' | 'status' | 'isFavorite'
>;
export type UpdatableTakeProperty = KeysMatching<TakeUpdateableProperties, any>;

export interface IUpdateTakePropertie {
    key: UpdatableTakeProperty;
    value: any;
}

export interface ITakeUpdate<K extends keyof TakeUpdateableProperties> {
    key: K;
    value: ITakeOutDTO[K];
}

export type bla = Partial<ITakeOutDTO>;

function updateTakeProperties<K extends keyof ITakeOutDTO>(
    project: any,
    sceneId: string,
    takeId: string,
    key: K,
    value: ITakeOutDTO[K]
): any {
    return {
        ...project,
        scenes: project.scenes.map((scene) =>
            scene.id === sceneId
                ? {
                      ...scene,
                      takes: scene.takes.map((take) =>
                          take.id === takeId
                              ? {
                                    ...take,
                                    [key]: value,
                                }
                              : take
                      ),
                  }
                : scene
        ),
    };
}

export interface ITakeContext extends ISceneContext {
    takeId: string;
}

export interface ITakeDynamicLottieContext extends ITakeContext {
    dynamicLottieId: string;
}

export enum TakeStatusEnum {
    NOT_RECORDED = 'not-recorded',
    RECORDING = 'recording',
    RECORDED = 'recorded',
}

export interface ITakeCopy {
    dynamicLottieChanges: IDynamicLottieChange[];
    transcripts?: ITranscript[];
}

export interface IBasicTake {
    id: string;
    startTime: number;
    endTime: number;
    duration: number;
    number: number;
    copy: ITakeCopy;
    videoLayers?: IBasicVideoLayer[];
    lottieLayers: ILottieOutDTO[];
    status: TakeStatusEnum;
    isFavorite: boolean;
    isPlaying?: boolean;
    chosenTranscript: ITranscript | null;
}

export interface ITakeOutDTO extends IBasicTake {
    lottieLayers: ILottieOutDTO[];
    videoLayers: IVideoLayerOutDTO[];
}

export interface ITakeInDTO extends IBasicTake {
    lottieLayers: ILottieLayerInDTO[];
    videoLayers?: IVideoLayerInDTO[];
}

export interface ITakeWithLottieComposed extends ITakeInDTO {
    title: string;
    lottieComposedConfigs: IComposeAndPlayOrNot;
    composeConfigsFreezed: IComposeAndPlayOrNot;
}

export interface ITakeConfigs extends ITakeWithLottieComposed {
    videoLayers: IVideoLayer[]; /// Locally to upload in server
    recordUniqueId: string; /// unique record id combined with media streamer
    toDisplayComposed: boolean;
}

/**
 * Client take that we transform from in to local, then from local to out
 */
export interface ITake extends ITakeConfigs {
    videoLayerDBController: VideoLayerIndexdbController;
    videoEditTake: VideoEditTake;

    addVideoLayerToTakeAsync(videoPositionId: string): Promise<{ take: ITakeInDTO; newVideoLayer: IVideoLayerInDTO }>;

    appendChunkToVideoLayerAsync(chunk: Blob, videoLayer: IVideoLayer): Promise<void>;

    setMediaRecorder(stream: MediaStream): MediaRecorder;

    updateVideoLayerTrimsAsync(
        videoLayerId: string,
        trimStart: number | null,
        trimEnd: number | null
    ): Promise<boolean>;

    get mediaRecorderOptions(): MediaRecorderOptions;

    get currentBaseMimetype(): string;

    toJSON(): any;

    clone(projectId: string): ITake;
}

export class Take implements ITake {
    videoLayers: IVideoLayer[];
    recordUniqueId: string;
    lottieComposedConfigs: IComposeAndPlayOrNot;
    composeConfigsFreezed: IComposeAndPlayOrNot;
    title: string;
    videoLayerDBController: VideoLayerIndexdbController;
    lottieLayers: ILottieLayerInDTO[];
    id: string;
    startTime: number;
    endTime: number;
    duration: number;
    number: number;
    copy: ITakeCopy;
    status: TakeStatusEnum;
    videoEditTake: VideoEditTake;
    toCleanAudio: boolean;
    toSegmentVideo: boolean;
    isFavorite: boolean;
    chosenTranscript: ITranscript | null;
    private recorderManager: TakeRecorderManager;
    toDisplayComposed: boolean;

    constructor(
        takeConfigs: ITakeConfigs,
        projectId: string,
        private sceneId: string,
        private sceneName: string,
        private isGraphic: boolean,
        videoLayerController: VideoLayerIndexdbController,
        private videoLayerApi: VideoLayerApiService,
        private profileService: ProfileService,
        private trims: { trimStart: number; trimEnd: number } | null
    ) {
        Object.assign(this, takeConfigs);

        this.recorderManager = new TakeRecorderManager(
            projectId,
            sceneId,
            this.id,
            this.videoLayerApi,
            this.profileService,
            null
        );
        this.videoLayerDBController = videoLayerController;
        this.videoEditTake = this.getVideoEditTake(sceneId, sceneName, this.isGraphic);
    }

    updateVideoLayerTrimsAsync(
        videoLayerId: string,
        trimStart: number | null,
        trimEnd: number | null
    ): Promise<boolean> {
        return this.recorderManager.updateVideoLayerTrimsAsync(videoLayerId, trimStart, trimEnd);
    }

    addVideoLayerToTakeAsync(videoPositionId: string) {
        return this.recorderManager.addVideoLayerToTakeAsync(videoPositionId);
    }

    appendChunkToVideoLayerAsync(chunk: Blob, videoLayer: IVideoLayer): Promise<void> {
        return this.recorderManager.appendChunkToVideoLayerAsync(chunk, videoLayer.id);
    }

    setMediaRecorder(stream: MediaStream): MediaRecorder {
        return this.recorderManager.setMediaRecorder(stream);
    }

    // Override toJSON to exclude recorderManager
    toJSON() {
        const { recorderManager, videoLayerApi, profileService, videoLayerDBController, videoEditTake, ...rest } = this;
        return rest;
    }

    clone(projectId: string): ITake {
        // Custom serialization to avoid circular references
        const serializedTake = JSON.stringify(this.toJSON());

        // Parse the serialized string back into an object
        const clonedData = JSON.parse(serializedTake) as ITakeConfigs;

        // Re-create the Take instance using the constructor
        const clonedTake = new Take(
            clonedData,
            projectId,
            this.sceneId,
            this.sceneName,
            this.isGraphic,
            this.videoLayerDBController,
            this.videoLayerApi,
            this.profileService,
            this.trims
        );

        // Update additional properties that are not passed through the constructor
        clonedTake.videoEditTake = this.getVideoEditTake(this.sceneId, this.sceneName, this.isGraphic);

        return clonedTake;
    }

    getVideoEditTake(sceneId: string, sceneName: string, isGraphic: boolean) {
        const newLottieComposedConfigs: IComposeAndPlayOrNot = JSON.parse(JSON.stringify(this.lottieComposedConfigs));
        const takeWithComposeConfigs: ITakeWithLottieComposed = {
            ...this,
            title: this.title,
            lottieComposedConfigs: newLottieComposedConfigs,
        };
        const trimsToApply = this.trims;
        const trims: ITrimLayers = {
            videoTrims: {
                start: trimsToApply?.trimStart ?? 0,
                end: trimsToApply?.trimEnd ?? 0,
            },
            lottieTrims: {
                start: 0,
                end: trimsToApply?.trimEnd ?? 0,
            },
        };
        const localTakeToList = new VideoEditTake(takeWithComposeConfigs, {}, sceneId, sceneName, trims, isGraphic);
        return localTakeToList;
    }

    get mediaRecorderOptions(): MediaRecorderOptions {
        return this.recorderManager.mediaRecorderOptions;
    }

    get currentBaseMimetype(): string {
        return this.recorderManager.currentBaseMimetype;
    }
}
